import { initAlertNotification } from 'components/alert-notification'
import { toggleClass } from 'utils/toggle-classlist'
import {
  checkedValidator,
  emailValidator,
  emptyValidator,
  maxValueValidator,
  minValueValidator,
} from 'services/validators-functions'
import { promiseWrapper } from 'utils/promise'
import { checkCheckbox, checkEmail, checkNumber, clearValidationError } from 'services/input-validators'
import { isJson } from 'utils/json'
import eventsService from 'services/events'
import alertHelper from 'services/alert-helper'
import piwikService from 'services/piwik'
import { pushDataLayer } from 'utils/data-layer-push'
import { getCurrentLanguage } from 'utils/current-language'
import { t9n } from 't9n/t9n'

declare let USER_BASE_URL_IK: string
declare let window: any

interface FormPayload {
  currency_pair: string
  my_price: number
  email: string
  layout: 'IK'
  current_price_must_be: string
  language: string
}

const $openAlertModal = document.querySelectorAll<HTMLDivElement>('[data-alert-open-modal]')
const $alertModal = document.querySelector<HTMLElement>('[data-js-selector="alert-modal"]')
const $modalCloseIcon = $alertModal?.querySelector<HTMLElement>('[data-js-selector="modal-close-icon"]')
const $modalCloseButton = $alertModal?.querySelector<HTMLElement>('[data-js-selector="modal-close-button"]')
const $modalCancelButton = $alertModal?.querySelector<HTMLElement>('[data-js-selector="modal-cancel-button"]')

const $form = document.querySelector<HTMLElement>('[data-js-selector="alert-form"]')
const $modalStep2 = document.querySelector<HTMLElement>('[data-js-selector="alert-modal__step2"]')
const $backendAnyErrorBlock = document.querySelector<HTMLDivElement>('[data-js-selector="backend-any-alert-error"]')
const $priceErrorBlock = document.querySelector<HTMLDivElement>('[data-js-selector="price-alert-error"]')

const $checkboxInput = document.querySelector<HTMLInputElement>('#alert-permission')
const $checkmarkWrapper = document.querySelector<HTMLDivElement>('[data-js-selector="checkmark-wrapper"]')
const $checkmarkIcon = document.querySelector<HTMLDivElement>('[data-js-selector="checkmark-icon"]')
const $checkboxErrorWrapper = document.querySelector<HTMLDivElement>('[data-js-selector="checkbox-error-message"]')

const $numericInput = document.querySelector<HTMLInputElement>('[data-rate-notification-input]')
const $emailInput = document.querySelector<HTMLInputElement>('[name="alert-email"]')
const $emailErrorWrapper = document.querySelector<HTMLDivElement>('[data-js-selector="email-error-message"]')

const $alertSelect = document.querySelector<HTMLInputElement>('#alert-select')
const $currencyElements = document.querySelectorAll<HTMLSpanElement>('[data-js-selector="chosen-currency"]')
const $mustBeResult = document.querySelector<HTMLSpanElement>('[data-js-selector="must-be"]')
const $rateResult = document.querySelector<HTMLSpanElement>('[data-js-selector="selected-rate"]')
const $emailResult = document.querySelector<HTMLElement>('[data-js-selector="email-result"]')

const isAlertURL = (): boolean => window.location.hash.includes(t9n('currencyAlert.alertHash'))

const getInitAlertCurrency = (): string => $alertModal.dataset.initAlertCurrency || 'EUR'

let recaptachaResponse = ''

const recaptachaVerifyCallback = (response): void => {
  recaptachaResponse = response
}

window.recaptchaApiLoaded = (): void => {
  window.grecaptcha.render(document.querySelector<HTMLDivElement>('#recaptcha'), {
    sitekey: '6LcWGb8mAAAAAOYt6eqFKeGU_Afyckoa71svuzrL',
    callback: recaptachaVerifyCallback,
    theme: 'light',
  })
}

const loadRecaptcha = (): void => {
  if (!document.getElementById('recaptchaScript')) {
    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?onload=recaptchaApiLoaded&render=explicit&hl=${getCurrentLanguage()}`
    script.defer = true
    script.setAttribute('id', 'recaptchaScript')
    document.head.append(script)
  }
}

const recaptachaReset = (): void => {
  window.grecaptcha.reset()
  recaptachaResponse = ''
}

const recaptachaShowError = (): void => {
  document.querySelector<HTMLElement>("[data-error-message='required-recaptacha']").hidden = false
}

const recaptachaResetError = (): void => {
  document.querySelector<HTMLElement>("[data-error-message='required-recaptacha']").hidden = true
}

function openModal(): void {
  loadRecaptcha()
  $alertModal.hidden = false
}

function closeModal(): void {
  if ($modalStep2.classList.contains('is-hidden')) {
    piwikService.pushPiwikNotification(['exchange-rates-notify', 'modal-closed', 'clicked-button'])
  }

  $alertModal.hidden = true
  $form.classList.remove('is-hidden')
  $modalStep2.classList.add('is-hidden')

  if ($checkboxInput.checked) {
    eventsService.simulateClick($checkboxInput.closest('label'))
  }
}

function toggleCheckbox(): void {
  toggleClass($checkmarkWrapper, 'is-checked')
  toggleClass($checkmarkIcon, 'is-hidden')
}

function toggleModal(): void {
  toggleClass($form, 'is-hidden')
  toggleClass($modalStep2, 'is-hidden')
}

function clearError(event): void {
  switch (event.target.name) {
    case 'alert-email':
      clearValidationError($emailInput, $emailErrorWrapper)
      break
    case 'alert-permission':
      clearValidationError($checkmarkWrapper, $checkboxErrorWrapper)
      break
    case 'alert-number':
      clearValidationError($numericInput, $priceErrorBlock)
      break
  }
}

function clearBackendError(): void {
  $backendAnyErrorBlock.innerHTML = ''
  $priceErrorBlock.innerHTML = ''
  $emailErrorWrapper.innerHTML = ''
}

function onSelectChange(): void {
  for (const currencyEl of $currencyElements) {
    currencyEl.innerHTML = $alertSelect.value
  }
}

function setResultValues(): void {
  $mustBeResult.innerHTML =
    alertHelper.getCheckedRadioValue().toUpperCase() === 'LOWER'
      ? t9n('currencyAlert.isLower')
      : t9n('currencyAlert.isHigher')
  $emailResult.innerHTML = $emailInput.value
  $rateResult.innerHTML = $numericInput.value
}

function isFormValid(): boolean {
  return (
    !emptyValidator($emailInput.value) &&
    !emailValidator($emailInput.value) &&
    !emptyValidator($numericInput.value) &&
    !minValueValidator('0,0001')($numericInput.value) &&
    !maxValueValidator('99,9999')($numericInput.value) &&
    !checkedValidator($checkboxInput.checked) &&
    recaptachaResponse.length !== 0
  )
}

function handleError(error: XMLHttpRequest): void {
  if (isJson(error.response)) {
    const resp = JSON.parse(error.response)
    const [prop] = Object.keys(resp.errors || {})
    switch (prop) {
      case 'my_price':
        $priceErrorBlock.innerHTML = t9n('currencyAlert.mustBeLessThan')
        break
      case 'email':
        $emailErrorWrapper.innerHTML =
          resp.errors.email.msg === 'ALERT_ALREADY_EXISTS'
            ? t9n('currencyAlert.alertAlreadyExists')
            : t9n('currencyAlert.maxAlerts')
        break
      default:
        $backendAnyErrorBlock.innerHTML = t9n('currencyAlert.error')
        break
    }
  } else {
    $backendAnyErrorBlock.innerHTML = t9n('currencyAlert.error')
  }
}

function onSubmit(event: Event): void {
  event.preventDefault()

  recaptachaResetError()
  checkNumber($numericInput, $priceErrorBlock, '0,0001', '99,9999')
  checkEmail($emailInput, $emailErrorWrapper)
  checkCheckbox($checkboxInput, $checkboxErrorWrapper, $checkmarkWrapper)
  if (recaptachaResponse.length === 0) recaptachaShowError()

  if (isFormValid()) {
    const payload: FormPayload = {
      currency_pair: `${$alertSelect.value}_PLN`,
      my_price: parseFloat($numericInput.value),
      email: $emailInput.value,
      layout: 'IK',
      current_price_must_be: alertHelper.getCheckedRadioValue(),
      language: getCurrentLanguage(),
    }

    sendData(payload)
      .then(() => {
        piwikService.pushPiwikNotification(['exchange-rates-notify', 'subscribed', 'clicked-button'])

        pushDataLayer('form_submitted', { form_name: 'Formularz alert walutowy' })

        clearBackendError()
        toggleModal()
        setResultValues()
        recaptachaReset()
      })
      .catch(handleError)
  }
}

function sendData(payload: FormPayload): Promise<void> {
  return promiseWrapper<void, FormPayload>(
    `${USER_BASE_URL_IK}/api/public/exchange-rates-notify/subscribe/`,
    'POST',
    payload,
  )
}

export default function initAlertModal(isMarketFetch = false): void {
  if ($alertModal) {
    eventsService.dispatchCustomEvent('alert-select', { value: getInitAlertCurrency() })

    $openAlertModal.forEach((modal) =>
      modal.addEventListener('click', () => {
        piwikService.pushPiwikNotification([
          'exchange-rates-notify',
          'modal-opened',
          `clicked-button-exchange-${getInitAlertCurrency()}`,
        ])
        openModal()
      }),
    )
    $modalCloseIcon?.addEventListener('click', closeModal)
    $modalCloseButton?.addEventListener('click', closeModal)
    $modalCancelButton?.addEventListener('click', closeModal)

    initAlertNotification(isMarketFetch)

    onSelectChange()

    $checkboxInput?.addEventListener('change', toggleCheckbox)
    $checkboxInput?.addEventListener('change', clearError)
    $emailInput?.addEventListener('input', clearError)
    $numericInput?.addEventListener('input', clearError)
    window.addEventListener('alert-select change', onSelectChange)
    $form?.addEventListener('submit', onSubmit)

    if (isAlertURL()) {
      piwikService.pushPiwikNotification(['exchange-rates-notify', 'modal-opened', 'auto-open'])
      openModal()
    }
  }
}
